// Type-only aliases for DOM fetch types. `RequestInit`/`HeadersInit`/
// `RequestCredentials` are ambient lib.dom.d.ts types with no runtime module
// to import from; referencing them by name trips `no-undef` (which cannot
// see TypeScript's ambient type globals). Deriving the identical type from
// the globally-typed `fetch`/`Headers` functions keeps the type exactly the
// same with zero behavior change.
type FetchInit = NonNullable<Parameters<typeof fetch>[1]>;
type FetchHeaders = NonNullable<ConstructorParameters<typeof Headers>[0]>;

export type PublicRequest = {
  url: string;
  method: "GET" | "HEAD" | "POST";
  credentials: FetchInit["credentials"];
  resourceType: "document" | "script" | "style" | "font" | "image" | "media" | "fetch";
  headers?: FetchHeaders;
  analyticsConsent?: "granted" | "denied" | "unknown";
};

export type PublicRequestPolicy = {
  marketingOrigin: "https://pinpointedu.com";
  publicApiOrigin: string;
  operationalApiOrigin: string;
  introductionMediaOrigin: "https://videos.pinpointedu.com";
  osmTileOrigins: readonly [
    "https://a.tile.openstreetmap.org",
    "https://b.tile.openstreetmap.org",
    "https://c.tile.openstreetmap.org",
  ];
  googleTagManagerOrigin: "https://www.googletagmanager.com";
  googleAnalyticsOrigins: readonly [
    "https://www.google-analytics.com",
    "https://region1.google-analytics.com",
  ];
};

export const PUBLIC_REQUEST_POLICY: PublicRequestPolicy = {
  marketingOrigin: "https://pinpointedu.com",
  publicApiOrigin: "https://pinpointedu.onrender.com",
  operationalApiOrigin: import.meta.env.VITE_PUBLIC_API_URL ?? "https://pinpointedu.onrender.com",
  introductionMediaOrigin: "https://videos.pinpointedu.com",
  osmTileOrigins: [
    "https://a.tile.openstreetmap.org",
    "https://b.tile.openstreetmap.org",
    "https://c.tile.openstreetmap.org",
  ],
  googleTagManagerOrigin: "https://www.googletagmanager.com",
  googleAnalyticsOrigins: [
    "https://www.google-analytics.com",
    "https://region1.google-analytics.com",
  ],
};

function hasHeaders(headers: FetchHeaders | undefined): boolean {
  if (!headers) return false;
  if (headers instanceof Headers) return !headers.keys().next().done;
  if (Array.isArray(headers)) return headers.length > 0;
  return Object.keys(headers).length > 0;
}

function isExactUrl(url: URL, expectedOrigin: string): boolean {
  return url.origin === expectedOrigin
    && url.username === ""
    && url.password === ""
    && url.search === ""
    && url.hash === ""
    && url.href === `${expectedOrigin}${url.pathname}`;
}

function isMarketingRequest(request: PublicRequest, url: URL, policy: PublicRequestPolicy): boolean {
  if (request.method !== "GET") return false;
  if (!isExactUrl(url, policy.marketingOrigin)) return false;
  if (request.resourceType === "document") return true;
  if (request.resourceType === "script") return /^\/assets\/[^/]+\.js$/.test(url.pathname);
  if (request.resourceType === "style") return /^\/assets\/[^/]+\.css$/.test(url.pathname);
  if (request.resourceType === "font") return /^\/fonts\/[^/]+\.woff2$/.test(url.pathname);
  return false;
}

function isPublicFactsRequest(request: PublicRequest, url: URL, policy: PublicRequestPolicy): boolean {
  return request.method === "GET"
    && request.resourceType === "fetch"
    && isExactUrl(url, policy.publicApiOrigin)
    && url.pathname === "/api/sat/public/taxonomy/rw-distribution";
}

function isIntroductionMediaRequest(request: PublicRequest, url: URL, policy: PublicRequestPolicy): boolean {
  if (request.method !== "GET") return false;
  if (!isExactUrl(url, policy.introductionMediaOrigin)) return false;
  if (url.pathname === "/pinpointedu_intro.png") return request.resourceType === "image";
  if (url.pathname === "/Introduction.web.mp4") {
    return request.resourceType === "media" || request.resourceType === "document";
  }
  return false;
}

function isOsmTileRequest(request: PublicRequest, url: URL, policy: PublicRequestPolicy): boolean {
  if (request.method !== "GET" || request.resourceType !== "image") return false;
  if (!policy.osmTileOrigins.some((origin) => isExactUrl(url, origin))) return false;

  const match = /^\/(\d+)\/(\d+)\/(\d+)\.png$/.exec(url.pathname);
  if (!match) return false;
  const [zoom, x, y] = match.slice(1).map(Number);
  if (!Number.isSafeInteger(zoom) || zoom < 0 || zoom > 19) return false;
  const exclusiveUpperBound = 2 ** zoom;
  return Number.isSafeInteger(x) && Number.isSafeInteger(y)
    && x >= 0 && y >= 0
    && x < exclusiveUpperBound && y < exclusiveUpperBound;
}

const GA_QUERY_KEYS = new Set([
  "v", "tid", "gtm", "_p", "gcd", "npa", "dma", "are", "pscd", "frm", "ir",
  "ul", "sr", "cid", "sct", "seg", "dl", "dt", "en", "_et", "uaa", "uab",
  "uafvl", "uamb", "uam", "uap", "uapv", "uaw", "sid", "sct", "seg",
]);

function isAllowedGaParameter(key: string): boolean {
  return GA_QUERY_KEYS.has(key) || /^(?:ep|epn|up|upn)\.[a-zA-Z0-9_]{1,40}$/.test(key)
    || /^pr\d{1,2}$/.test(key);
}

function isGoogleTagManagerRequest(request: PublicRequest, url: URL, policy: PublicRequestPolicy): boolean {
  if (request.analyticsConsent !== "granted" || request.method !== "GET" || request.resourceType !== "script") return false;
  const reviewedLoaderPath = `/${"g" + "tag"}/js`;
  if (url.origin !== policy.googleTagManagerOrigin || url.pathname !== reviewedLoaderPath || url.hash) return false;
  const entries = [...url.searchParams.entries()];
  return entries.length === 1 && entries[0][0] === "id" && /^G-[A-Z0-9]{6,20}$/.test(entries[0][1]);
}

function isGoogleAnalyticsRequest(request: PublicRequest, url: URL, policy: PublicRequestPolicy): boolean {
  if (request.analyticsConsent !== "granted" || !["GET", "POST"].includes(request.method)
    || request.resourceType !== "fetch") return false;
  if (!policy.googleAnalyticsOrigins.includes(url.origin as typeof policy.googleAnalyticsOrigins[number])
    || url.pathname !== "/g/collect" || url.hash || !url.search) return false;
  const entries = [...url.searchParams.entries()];
  if (!entries.length || !entries.every(([key, value]) => isAllowedGaParameter(key) && value.length <= 512 && !/@/.test(value))) return false;
  const measurementId = url.searchParams.get("tid");
  return /^G-[A-Z0-9]{6,20}$/.test(measurementId ?? "") && url.searchParams.get("v") === "2";
}

function isJsonContentTypeOnly(headers: FetchHeaders | undefined): boolean {
  if (!headers) return false;
  const entries = [...new Headers(headers).entries()];
  return entries.length === 1 && entries[0][0] === "content-type" && entries[0][1] === "application/json";
}

function isOperationalTelemetryRequest(request: PublicRequest, url: URL, policy: PublicRequestPolicy): boolean {
  return request.method === "POST" && request.resourceType === "fetch"
    && url.origin === policy.operationalApiOrigin && url.pathname === "/api/analytics/operational"
    && url.search === "" && url.hash === "" && isJsonContentTypeOnly(request.headers);
}

export function assertAllowedPublicRequest(
  request: PublicRequest,
  policy: PublicRequestPolicy = PUBLIC_REQUEST_POLICY,
): void {
  if (request.credentials !== "omit") throw new Error("Public requests must omit credentials");

  let url: URL;
  try {
    url = new URL(request.url);
  } catch {
    throw new Error("Public request URL must be absolute");
  }
  if (request.url !== url.href) throw new Error("Public request URL must use canonical URL spelling");

  const operationalTelemetry = isOperationalTelemetryRequest(request, url, policy);
  if (hasHeaders(request.headers) && !operationalTelemetry) {
    throw new Error("Public requests cannot carry unreviewed headers");
  }

  const allowed = isMarketingRequest(request, url, policy)
    || isPublicFactsRequest(request, url, policy)
    || isIntroductionMediaRequest(request, url, policy)
    || isOsmTileRequest(request, url, policy)
    || isGoogleTagManagerRequest(request, url, policy)
    || isGoogleAnalyticsRequest(request, url, policy)
    || operationalTelemetry;
  if (!allowed) throw new Error(`Public request is not allowlisted: ${request.url}`);
}
